import React from "react";
import type {
  ButtonProps,
  ButtonShape,
  ButtonSize,
  TertiaryButtonProps,
} from "@sprout/buttons/types.ts";
import classNames from "classnames";
import LoadingSpinner from "@sprout/loading_spinner.tsx";

type InternalButtonShape = ButtonShape | "none";
type InternalButtonSize = ButtonSize | "none";

type InternalButtonProps = Omit<ButtonProps, "shape" | "size"> &
  Pick<React.ComponentProps<"button">, "className"> & {
    shape?: InternalButtonShape;
    size?: InternalButtonSize;
  };

type InternalTertiaryButtonProps = TertiaryButtonProps &
  Pick<React.ComponentProps<"button">, "className">;

const InternalButton = ({
  ariaLabel,
  children,
  className,
  disabled = false,
  loading = false,
  onClick,
  shape = "rounded",
  size = "large",
  type = "button",
  ...restProps
}: InternalButtonProps) => {
  const mergedClassName = classNames(
    {
      btn: true,
      [`btn--${shape || "rounded"}`]: shape !== "none",
      [`btn--${size || "large"}`]: size !== "none",
      btn__disabled: disabled,
      btn__loading: loading,
    },
    className,
  );

  const handleClick = (event: React.MouseEvent<Element, MouseEvent>) => {
    if (!onClick || disabled || loading) {
      return;
    }

    onClick(event);
  };

  return (
    <button
      aria-label={ariaLabel}
      className={mergedClassName}
      onClick={handleClick}
      type={type}
      {...restProps}
    >
      {loading && (
        <span>
          <LoadingSpinner size="small" />
        </span>
      )}
      <span>{children}</span>
    </button>
  );
};

const InternalTertiaryButton = ({
  className,
  secondary,
  medium,
  ...props
}: InternalTertiaryButtonProps) => {
  const mergedClassName = classNames("btn__tertiary", className, {
    ["btn__tertiary--secondary"]: secondary || false,
    ["btn__tertiary--medium"]: medium || false,
  });

  return <InternalButton {...props} shape="none" size="none" className={mergedClassName} />;
};

export const PrimaryButton = (props: ButtonProps) => {
  return <InternalButton {...props} className="btn__primary" />;
};

export const SecondaryButton = (props: ButtonProps) => {
  return <InternalButton {...props} />;
};

export const DangerButton = (props: ButtonProps) => {
  return <InternalButton {...props} className="btn__danger" />;
};

export const DestructiveButton = (props: ButtonProps) => {
  return <InternalButton {...props} className="btn__destructive" />;
};

export const TertiaryButton = (props: TertiaryButtonProps) => {
  return <InternalTertiaryButton {...props} />;
};

export const TertiaryDangerButton = (props: TertiaryButtonProps) => {
  return <InternalTertiaryButton {...props} className="btn__danger" />;
};

SecondaryButton.displayName = "SecondaryButton";
